/**
 * **A gift card's code** — the one alphabet, in the one place both minters can
 * reach (spec §7.3).
 *
 * Two things mint a code and they must mint the same shape: `issueCertificate`
 * in `packages/store` (for any caller that sends none) and the staff app's
 * Gift cards sheet, which mints it **on the device** so the owner can read it
 * off the confirmation to the buyer before the iPad has signal — and sends it
 * as `code`, so what she read aloud is what the server stores. The server still
 * validates it and still refuses a duplicate (`certificates_tenant_code_key`).
 *
 * The alphabet is Crockford's minus the vowels: a card is read aloud over a
 * phone and typed at a counter by somebody holding it, so `0/O`, `1/I/L` and
 * anything that spells a word are support calls waiting to happen. Twelve
 * characters in three groups of four, uniformly from a cryptographic source,
 * because a guessable certificate is money: 30¹² ≈ 5·10¹⁷, against her old
 * LoveSpa app's `GC-` plus four `Math.random()` digits — nine thousand codes a
 * patient guest could walk. Those `GC-1234` codes keep working exactly as they
 * were imported; they are simply not how a new one is made.
 */
export const CERTIFICATE_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const CERTIFICATE_CODE_LENGTH = 12;
/**
 * Twelve characters from `random(max)` — which must be uniform on `[0, max)`
 * and unpredictable (`crypto.randomInt` on the server, `randomIndex` below on
 * a device). Formatted `XXXX-XXXX-XXXX`.
 */
export function mintCertificateCode(random) {
    const chars = [];
    for (let i = 0; i < CERTIFICATE_CODE_LENGTH; i += 1) {
        chars.push(CERTIFICATE_CODE_ALPHABET[random(CERTIFICATE_CODE_ALPHABET.length)]);
    }
    return `${chars.slice(0, 4).join('')}-${chars.slice(4, 8).join('')}-${chars.slice(8, 12).join('')}`;
}
/**
 * A uniform index in `[0, max)` from Web Crypto — `crypto.getRandomValues`,
 * which a browser, a PWA and Node 24 all have as a global. Rejection sampling
 * rather than `% max`, because 2³² is not a multiple of 30 and a modulo would
 * make the first few letters of the alphabet a hair likelier than the rest.
 */
export function cryptoRandomIndex(max) {
    if (!Number.isInteger(max) || max <= 0 || max > 0x1_0000_0000) {
        throw new RangeError(`cryptoRandomIndex: max must be an integer in 1..2^32, got ${max}`);
    }
    const limit = Math.floor(0x1_0000_0000 / max) * max;
    const word = new Uint32Array(1);
    for (;;) {
        globalThis.crypto.getRandomValues(word);
        const value = word[0];
        if (value < limit)
            return value % max;
    }
}
/** `POST /v1/certificates`' `code` rule, restated for a device that wants to say so first. */
export const CERTIFICATE_CODE_MIN = 4;
export const CERTIFICATE_CODE_MAX = 64;
/**
 * A code **printed on a physical card**, as the owner typed it — uppercased,
 * with spaces (people type "gc 3632") turned into the dash the card shows, and
 * a stray leading or trailing dash dropped. `null` when what is left is not a
 * code the server would accept: letters, digits and single dashes between
 * them, 4..64 characters.
 *
 * Nothing else is rewritten, deliberately — no `GC-` added to a bare number.
 * Whatever is printed on the card is what somebody will type at the till to
 * spend it, and the till compares codes exactly (case aside); a code the
 * device "helpfully" prefixed would be a card that cannot be found by the
 * number written on it.
 */
export function normalizePrintedCertificateCode(raw) {
    const code = raw
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');
    if (code.length < CERTIFICATE_CODE_MIN || code.length > CERTIFICATE_CODE_MAX)
        return null;
    return /^[A-Z0-9]+(?:-[A-Z0-9]+)*$/.test(code) ? code : null;
}
/**
 * The code in groups a person can read aloud — "ABCD EFGH JKMN", "GC 3632".
 * Split on the dashes the code carries, and a run longer than four (a printed
 * "GC36324471") into fours. **Display only**: what is copied, sent and looked
 * up is always the code itself.
 */
export function certificateCodeGroups(code) {
    const groups = [];
    for (const part of code.split('-')) {
        if (part === '')
            continue;
        for (let i = 0; i < part.length; i += 4)
            groups.push(part.slice(i, i + 4));
    }
    return groups;
}
//# sourceMappingURL=certificate-code.js.map